import React, { useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  Select,
  MenuItem,
  FormControl,
  IconButton,
  useMediaQuery,
  useTheme,
  SwipeableDrawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
} from '@mui/material';
import {
  NotificationsOutlined as MentionsIcon,
  AccountCircleOutlined as ProfileIcon,
  LogoutOutlined as LogoutIcon,
  LoginOutlined as LoginIcon,
  GroupOutlined as IdentitiesIcon,
  HelpOutline as FAQIcon,
  Security as SecurityIcon,
  Info as HowItWorksIcon,
  Menu as MenuIcon,
  AdminPanelSettings as AdminIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { useLanguageUrl, useLanguageChange, LanguageCode, safeLanguageCode } from '../utils/languageUtils';
import { isAdminUser } from '../utils/authUtils';
import Login from './Login';
import ShieldIcon from './ShieldIcon';

interface Language {
  code: string;
  name: string;
}

interface NavItem {
  label: string;
  icon: React.ReactElement;
  url: string;
  action?: () => void;
}

const LANGUAGES: Language[] = [
  { code: 'en', name: 'English' },
  { code: 'es', name: 'Español' },
];

const Navigation: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { isAuthenticated, user, logout, token } = useAuth();
  const [loginOpen, setLoginOpen] = useState(false);
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleNavClick = (action?: () => void) => {
    scrollToTop();
    if (action) {
      action();
    }
  };
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const getLanguageUrl = useLanguageUrl();
  const changeLanguage = useLanguageChange();

  // Check if user is admin
  const isAdmin = isAdminUser(token);

  // Mobile drawer state management
  const [mobileOpen, setMobileOpen] = useState(false);
  const iOS = typeof navigator !== 'undefined' && /iPad|iPhone|iPod/.test(navigator.userAgent);

  const handleDrawerToggle = useCallback(() => {
    setMobileOpen(prev => !prev);
  }, []);

  const handleDrawerClose = useCallback(() => {
    setMobileOpen(false);
  }, []);

  const handleLanguageChange = (event: any) => {
    changeLanguage(safeLanguageCode(event.target.value));
  };

  const handleLoginClick = () => {
    setLoginOpen(true);
  };

  const handleLoginClose = () => {
    setLoginOpen(false);
  };

  const handleLogout = () => {
    logout();
  };

  // Define navigation items based on authentication state
  const authenticatedNavItems: NavItem[] = [
    {
      label: t('mentions.title'),
      icon: <MentionsIcon />,
      url: getLanguageUrl('mentions'),
    },
    {
      label: t('identities.title'),
      icon: <IdentitiesIcon />,
      url: getLanguageUrl('monitoredIdentities'),
    },
    ...(isAdmin ? [{
      label: t('admin:backoffice.title', 'Admin'),
      icon: <AdminIcon />,
      url: `/${i18n.language}/admin`,
    }] : []),
    {
      label: user?.fullName || user?.email || t('nav.profile'),
      icon: <ProfileIcon />,
      url: getLanguageUrl('accountPreferences'),
    },
    {
      label: t('auth.logout'),
      icon: <LogoutIcon />,
      url: '',
      action: handleLogout,
    },
  ];

  const guestNavItems: NavItem[] = [
    {
      label: t('nav.howItWorks'),
      icon: <HowItWorksIcon />,
      url: getLanguageUrl('howItWorks'),
    },
    {
      label: t('nav.faq'),
      icon: <FAQIcon />,
      url: getLanguageUrl('faq'),
    },
    {
      label: t('nav.whyMonitoring'),
      icon: <SecurityIcon />,
      url: getLanguageUrl('whyMonitoringMatters'),
    },
  ];

  const navItems = isAuthenticated ? authenticatedNavItems : guestNavItems;

  // Drawer content
  const drawerContent = (
    <Box onClick={handleDrawerClose} sx={{ textAlign: 'center' }}>
      <Typography 
        variant="h6" 
        sx={{ 
          my: 2,
          color: 'primary.main',
          fontWeight: 700,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 1,
        }}
      >
        <ShieldIcon sx={{ fontSize: '1.5rem' }} /> {t('app.title')}
      </Typography>
      <Divider />
      <List>
        {navItems.map((item, index) => (
          <ListItem key={index} disablePadding>
            <ListItemButton
              component={item.url ? Link : 'button'}
              to={item.url || undefined}
              onClick={() => {
                if (item.action) {
                  handleNavClick(item.action);
                } else if (item.url) {
                  handleNavClick();
                }
                handleDrawerClose();
              }}
              sx={{
                color: 'primary.main',
                '&:hover': { 
                  backgroundColor: 'primary.main', 
                  color: 'white',
                  '& .MuiListItemIcon-root': {
                    color: 'white',
                  },
                },
                borderRadius: 2,
                mx: 1,
                my: 0.5,
              }}
            >
              <ListItemIcon sx={{ color: 'inherit', minWidth: 40 }}>
                {item.icon}
              </ListItemIcon>
              <ListItemText 
                primary={item.label} 
                slotProps={{
                  primary: { sx: { fontWeight: 500 } }
                }}
              />
            </ListItemButton>
          </ListItem>
        ))}
        {!isAuthenticated && (
          <>
            <Divider sx={{ my: 1 }} />
            <ListItem disablePadding>
              <ListItemButton
                onClick={() => {
                  handleLoginClick();
                  handleDrawerClose();
                }}
                sx={{
                  color: 'primary.main',
                  '&:hover': { 
                    backgroundColor: 'primary.main', 
                    color: 'white',
                    '& .MuiListItemIcon-root': {
                      color: 'white',
                    },
                  },
                  borderRadius: 2,
                  mx: 1,
                  my: 0.5,
                }}
              >
                <ListItemIcon sx={{ color: 'inherit', minWidth: 40 }}>
                  <LoginIcon />
                </ListItemIcon>
                <ListItemText 
                  primary={t('auth.login')} 
                  slotProps={{
                    primary: { sx: { fontWeight: 500 } }
                  }}
                />
              </ListItemButton>
            </ListItem>
          </>
        )}
      </List>
    </Box>
  );

  return (
    <AppBar 
      component="header"
      position="static" 
      sx={{ 
        backgroundColor: 'background.paper',
        boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
        borderBottom: '1px solid #e2e8f0',
      }}
    >
      <Toolbar component="nav" sx={{ py: 1 }}>
        {/* Logo Section */}
        <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
          <Typography 
            variant="h6" 
            component={Link}
            to={getLanguageUrl('home')}
            onClick={scrollToTop}
            sx={{ 
              color: 'primary.main',
              textDecoration: 'none',
              fontWeight: 700,
              display: 'flex',
              alignItems: 'center',
              gap: 1,
            }}
          >
            <ShieldIcon sx={{ fontSize: '1.5rem' }} /> {t('app.title')}
          </Typography>
        </Box>
        
        {/* Navigation Actions */}
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          {isAuthenticated ? (
            <>
              {isMobile ? (
                // Mobile: Hamburger menu button
                <IconButton
                  color="primary"
                  aria-label={t('navigation.openMenu')}
                  aria-expanded={mobileOpen}
                  edge="start"
                  onClick={handleDrawerToggle}
                  sx={{ mr: 2 }}
                >
                  <MenuIcon />
                </IconButton>
              ) : (
                // Desktop: Button with icon and text
                <Box sx={{ display: { xs: 'none', md: 'flex' }, gap: 2 }}>
                  <Button
                    color="primary"
                    component={Link}
                    to={getLanguageUrl('mentions')}
                    startIcon={<MentionsIcon />}
                    onClick={scrollToTop}
                    sx={{ textDecoration: 'none' }}
                  >
                    {t('mentions.title')}
                  </Button>
                  <Button
                    color="primary"
                    component={Link}
                    to={getLanguageUrl('monitoredIdentities')}
                    startIcon={<IdentitiesIcon />}
                    onClick={scrollToTop}
                    sx={{ textDecoration: 'none' }}
                  >
                    {t('identities.title')}
                  </Button>
                  {isAdmin && (
                    <Button
                      color="primary"
                      component={Link}
                      to={`/${i18n.language}/admin`}
                      startIcon={<AdminIcon />}
                      onClick={scrollToTop}
                      sx={{ textDecoration: 'none' }}
                    >
                      {t('admin:backoffice.title', 'Admin')}
                    </Button>
                  )}
                  <Button
                    color="primary"
                    component={Link}
                    to={getLanguageUrl('accountPreferences')}
                    startIcon={<ProfileIcon />}
                    onClick={scrollToTop}
                    sx={{ textDecoration: 'none' }}
                  >
                    {user?.fullName || user?.email}
                  </Button>
                  <Button
                    color="primary"
                    onClick={handleLogout}
                    startIcon={<LogoutIcon />}
                    sx={{ textDecoration: 'none' }}
                  >
                    {t('auth.logout')}
                  </Button>
                </Box>
              )}
            </>
          ) : (
            <>
              {isMobile ? (
                // Mobile: Hamburger menu button for unauthenticated users
                <IconButton
                  color="primary"
                  aria-label={t('navigation.openMenu')}
                  aria-expanded={mobileOpen}
                  edge="start"
                  onClick={handleDrawerToggle}
                  sx={{ mr: 2 }}
                >
                  <MenuIcon />
                </IconButton>
              ) : (
                // Desktop: Button with icon and text
                <Box sx={{ display: { xs: 'none', md: 'flex' }, gap: 2 }}>
                  <Button
                    color="primary"
                    component={Link}
                    to={getLanguageUrl('howItWorks')}
                    startIcon={<HowItWorksIcon />}
                    onClick={scrollToTop}
                    sx={{ textDecoration: 'none' }}
                  >
                    {t('nav.howItWorks')}
                  </Button>
                  <Button
                    color="primary"
                    component={Link}
                    to={getLanguageUrl('faq')}
                    startIcon={<FAQIcon />}
                    onClick={scrollToTop}
                    sx={{ textDecoration: 'none' }}
                  >
                    {t('nav.faq')}
                  </Button>
                  <Button
                    color="primary"
                    component={Link}
                    to={getLanguageUrl('whyMonitoringMatters')}
                    startIcon={<SecurityIcon />}
                    onClick={scrollToTop}
                    sx={{ textDecoration: 'none' }}
                  >
                    {t('nav.whyMonitoring')}
                  </Button>
                </Box>
              )}
              <Button
                variant="outlined"
                color="primary"
                onClick={handleLoginClick}
                startIcon={<LoginIcon />}
                sx={{ 
                  textDecoration: 'none',
                  borderRadius: 2,
                  px: 3,
                  display: { xs: 'none', md: 'inline-flex' },
                }}
              >
                {t('auth.login')}
              </Button>
            </>
          )}
          
          <FormControl size="small" sx={{ minWidth: 100 }}>
            <Select
              value={i18n.language}
              onChange={handleLanguageChange}
              displayEmpty
              sx={{ 
                color: 'text.primary',
                '.MuiOutlinedInput-notchedOutline': { 
                  borderColor: 'divider' 
                },
              }}
            >
              {LANGUAGES.map((language) => (
                <MenuItem key={language.code} value={language.code}>
                  {language.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Toolbar>

      {/* Mobile drawer */}
      <SwipeableDrawer
        variant="temporary"
        open={mobileOpen}
        onClose={handleDrawerClose}
        onOpen={() => setMobileOpen(true)}
        disableBackdropTransition={!iOS}
        disableDiscovery={iOS}
        slotProps={{
          paper: {
            sx: {
              boxSizing: 'border-box',
              width: 280,
              maxWidth: '80vw',
              backgroundColor: 'background.paper',
            }
          }
        }}
        sx={{ display: { xs: 'block', md: 'none' } }}
        role="navigation"
        aria-label={t('navigation.mobileMenu')}
      >
        {drawerContent}
      </SwipeableDrawer>
      
      <Login open={loginOpen} onClose={handleLoginClose} />
    </AppBar>
  );
};

export default Navigation;